import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { bookmarkApi, BookmarkCategory } from '../api/bookmark'
import { toastUtil } from '../utils/toast'
import { rememberBeforeCreate } from '../utils/platelistCreateReturn'
import PlatelistPickerSheet from './PlatelistPickerSheet'

interface SaveDestinationSheetProps {
  isOpen: boolean
  /**
   * 시트가 닫힐 때.
   *
   * pickedAny 는 "이 시트에서 리스트에 담은 적이 있나". 담았으면 그때마다
   * "○○에 담았어요" 를 이미 봤으므로, 화면 쪽에서 요약 토스트를 또 띄우면
   * 두 개가 겹친다. 아무것도 안 고르고 닫았을 때만 요약을 띄우라는 뜻이다.
   */
  onClose: (info: { pickedAny: boolean }) => void
  /**
   * 이미 '저장한 장소' 에 담긴 장소들. 시트는 이 장소들을 리스트에 넣고 뺀다.
   *
   * 저장 자체는 시트를 열기 **전에** 끝나 있다 — 저장 시트(PlaceSaveSheet)와
   * 같은 규칙이다. 그래서 완료 버튼이 없다.
   */
  placeIds: number[]
  /** 윗줄에 쓸 가게 이름. 한 곳이면 주소도 같이 */
  placeName?: string
  placeAddress?: string
  /**
   * 방금 만들고 돌아온 리스트.
   *
   * 이 시트에서 "추가" 로 나갔다 돌아왔다는 뜻이다. 그러려고
   * 만든 것이므로 장소를 담고 체크해 둔다.
   */
  justCreatedCategoryId?: string | null
  /**
   * 돌아올 자리를 가리는 이름.
   *
   * 리스트를 새로 만들러 갔다 오면 이 이름으로 체크 상태를 되찾는다.
   * 화면마다 달라야 한다 — 같으면 남의 쪽지를 읽는다.
   */
  returnKey: string
}

/**
 * 고른 장소를 **어디에 담을지** 고르는 시트.
 *
 * 인스타 공유 · 지도 링크 공유 · 가입 직후 불러오기가 같이 쓴다. 셋 다
 * "이미 고른 여러 곳을 한 번에 담는" 같은 일이라 화면이 같아야 한다.
 *
 * 껍데기는 저장 시트(PlaceSaveSheet)와 같은 PlatelistPickerSheet 다. 예전에는
 * 드롭다운 하나로 골랐는데, 접혀 있어서 리스트가 몇 개인지 커버가 뭔지 안 보였고
 * 저장 시트와 생김새가 전혀 달라 같은 앱 같지 않았다.
 *
 * 여기만의 것은 머리말과 "전부 들어 있을 때만 체크" 규칙 둘이다.
 * 고르지 않아도 '저장한 장소' 에는 들어간다(서버가 늘 넣는다). 그 사실을
 * 머리말에 적어둔다 — 예전 드롭다운은 그걸 숨겨서, 리스트를 고르면 저장한
 * 장소에는 안 들어가는 줄 알게 했다.
 */
const SaveDestinationSheet: React.FC<SaveDestinationSheetProps> = ({
  isOpen,
  onClose,
  placeIds,
  placeName,
  placeAddress,
  returnKey,
  justCreatedCategoryId = null,
}) => {
  const navigate = useNavigate()
  const [categories, setCategories] = useState<BookmarkCategory[]>([])
  /** 담긴 장소를 **모두** 갖고 있는 리스트 = 체크된 것 */
  const [memberOf, setMemberOf] = useState<Set<string>>(new Set())
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState(false)
  /** 이 시트에서 리스트에 담은 적이 있나 — 닫을 때 화면에 알려준다 */
  const pickedAny = useRef(false)

  useEffect(() => {
    if (isOpen) pickedAny.current = false
  }, [isOpen])

  const load = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await bookmarkApi.getBookmarks()
      const lists = res.categories.filter((c) => !c.is_default)
      setCategories(lists)

      /*
       * 여러 곳을 한 번에 다루므로 '담겼다' 의 기준을 정해야 한다.
       *
       * **전부 들어 있을 때만** 체크로 본다. 일부만 들어 있는데 체크로 보이면
       * 눌러서 뺐을 때 안 넣었던 것까지 빠진다. 반대로 체크가 비어 있으면
       * 눌렀을 때 빠진 것만 채워진다 — 어느 쪽이든 되돌릴 수 있다.
       */
      const wanted = placeIds.map(String)
      setMemberOf(
        new Set(
          lists
            .filter((c) => {
              const have = new Set(c.items.map((i) => i.id))
              return wanted.length > 0 && wanted.every((id) => have.has(id))
            })
            .map((c) => c.id)
        )
      )
    } catch (error) {
      console.error('플레이트리스트 목록 로드 실패:', error)
      setCategories([])
    } finally {
      setIsLoading(false)
    }
    // placeIds 는 매번 새 배열이라 길이와 내용으로만 비교한다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeIds.join(',')])

  useEffect(() => {
    if (!isOpen) return
    void load()
  }, [isOpen, load])

  /*
   * 방금 만든 리스트에 담는다.
   *
   * 목록을 다시 읽어 맞추는 대신 바로 체크해 둔다 — 서버 왕복을 기다리는
   * 동안 체크가 안 된 채로 보이면 "만들었는데 왜 안 담겼지" 가 된다.
   */
  useEffect(() => {
    if (!justCreatedCategoryId || placeIds.length === 0) return

    setMemberOf((prev) => new Set(prev).add(justCreatedCategoryId))
    pickedAny.current = true
    bookmarkApi
      .addPlacesToCategory(justCreatedCategoryId, placeIds)
      .catch(() => {
        setMemberOf((prev) => {
          const next = new Set(prev)
          next.delete(justCreatedCategoryId)
          return next
        })
        toastUtil.error('새 리스트에 담지 못했어요')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justCreatedCategoryId, placeIds.join(',')])

  /** 리스트 한 곳에 전부 넣거나 전부 뺀다 */
  const toggle = async (category: BookmarkCategory) => {
    if (pendingIds.has(category.id) || placeIds.length === 0) return
    const wasMember = memberOf.has(category.id)

    // 먼저 화면을 바꾸고 실패하면 되돌린다 (서버 왕복이 수백 ms)
    setPendingIds((prev) => new Set(prev).add(category.id))
    setMemberOf((prev) => {
      const next = new Set(prev)
      if (wasMember) next.delete(category.id)
      else next.add(category.id)
      return next
    })

    try {
      if (wasMember) {
        await bookmarkApi.removePlacesFromCategory(category.id, placeIds)
      } else {
        await bookmarkApi.addPlacesToCategory(category.id, placeIds)
        pickedAny.current = true
        // 담았다는 말은 시트가 닫힌 뒤에 한 번만 한다(PlatelistPickerSheet).
        // 여기서 띄우면 아직 떠 있는 시트에 가리고, 두 곳에 담으면 겹친다.
      }
    } catch (error) {
      setMemberOf((prev) => {
        const next = new Set(prev)
        if (wasMember) next.add(category.id)
        else next.delete(category.id)
        return next
      })
      toastUtil.error(wasMember ? '빼지 못했어요' : '담지 못했어요')
    } finally {
      setPendingIds((prev) => {
        const next = new Set(prev)
        next.delete(category.id)
        return next
      })
    }
  }

  const goCreate = () => {
    rememberBeforeCreate({ key: returnKey, checked: [], payload: { placeIds } })
    navigate('/platelist/new')
  }

  const count = placeIds.length
  const title =
    count <= 1
      ? placeName ?? '장소 1곳'
      : placeName
        ? `${placeName} 외 ${count - 1}곳`
        : `장소 ${count}곳`

  return (
    <PlatelistPickerSheet
      isOpen={isOpen}
      onClose={() => onClose({ pickedAny: pickedAny.current })}
      categories={categories}
      checkedIds={memberOf}
      isLoading={isLoading}
      onToggle={(category) => void toggle(category)}
      onCreate={goCreate}
      header={() => (
        <div className="w-full px-20px pb-20px flex flex-col gap-4px">
          <div className="font-pretendard font-bold text-16px leading-125% text-brown-80%-main truncate">
            {title}
          </div>
          {count <= 1 && placeAddress && (
            <div className="font-pretendard text-14px leading-125% text-[rgba(75,54,33,0.6)] truncate">
              {placeAddress}
            </div>
          )}
          <div className="font-pretendard text-14px leading-125% text-[rgba(75,54,33,0.6)]">
            플레이트리스트에 넣지 않아도 저장돼요
          </div>
        </div>
      )}
    />
  )
}

export default SaveDestinationSheet
